<template>
  <h1 class="hr-main fs-4 fw-normal mb-3">{{ t('title') }}</h1>

  <Grid
    v-model="lm"
    :total="model.total_count"
    :columns="baseColumns"
    :data="model.data || []"
    list-name="activities"
    @click:row="onRowClick"
    :pagination="true"
    :page-size-options="[5, 10, 20, 50]"
    :persist-key="persistKey"
  >
    <template #cell="{ row, col }">
      <template v-if="col.getter">
        {{ col.getter(row) }}
      </template>
      <template v-else-if="col.date">
        {{ commons.dateLocal(col.path ? _result(row, col.path) : row[col.title]) ?? '' }}
      </template>
      <template v-else-if="col.path">
        {{ pathLabel(row, col.path, _result(row, col.path), locale) }}
      </template>
      <template v-else-if="col.icon">
        <i
          class="bi fs-5"
          :class="row[col.aligned_class]"
        />
      </template>
      <template v-else>
        {{ row[col.title] }}
      </template>
    </template>
  </Grid>
</template>

<script setup>
import { result as _result } from 'lodash-es'
import { commons } from '@/lib/transformer'
import { dropdownLabel, pathLabel } from '@/lib/dropdownLabel.js'
import { useApi } from '@/composables/api'
import { useRouter } from 'vue-router'
import Grid from '@/components/list/grid.vue'
import { ref, reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useConfigsStore } from '@/stores/configs'

const router = useRouter()
const { t, locale } = useI18n()
const { config } = storeToRefs(useConfigsStore())

const props = defineProps({
  filter: {
    type: String,
    default: 'my',
  },
  modalstack: {
    type: Boolean,
    default: false,
  },
  persistKey: {
    type: String,
    default: '',
  },
})

const baseColumns = computed(() => {
  const loc = locale.value
  return [
    { title: 'id', label: 'ID', width: '30px' },
    {
      title: 'aligned',
      label: t('approved'),
      aligned_class: 'aligned_class',
      width: '70px',
      icon: true,
    },
    {
      title: 'name',
      label: t('medicine'),
      path: 'medicine_trademark.name',
      width: '160px',
    },
    {
      title: 'medicinal_form',
      label: t('medicinalForm'),
      path: 'medicine_trademark.medicinal_form.value',
      width: '250px',
    },
    {
      title: 'activity_subtype.parent_name',
      label: t('activity'),
      path: 'activity_subtype.parent_name',
      width: '130px',
    },
    {
      title: 'activity_subtype.name',
      label: t('activityType'),
      path: 'activity_subtype.name',
      width: '150px',
    },
    {
      title: 'created_by.username',
      label: t('responsible'),
      path: 'created_by.username',
      width: '150px',
      getter: o => o.created_by?.fio || o.created_by?.username,
    },
    {
      title: 'shipment_date_scheduled',
      label: t('plannedSubmissionDate'),
      path: 'shipment_date_scheduled',
      width: '160px',
      date: true,
    },
    {
      title: 'dispatch_date_executed',
      label: t('actualSubmissionDate'),
      path: 'order_out.dispatch_date_executed',
      width: '180px',
      date: true,
      getter: o => o.order_out?.dispatch_date_executed,
    },
    {
      title: 'activity_status',
      label: t('activityStatus'),
      path: 'status.value',
      width: '160px',
      getter: row => resolveStatusLabel(row?.status, config.value?.activities?.status, loc),
    },
    {
      title: 'status',
      label: t('orderStatus'),
      path: 'order.status.value',
      width: '160px',
      filter: false,
      getter: row =>
        [...(row?.order || [])]
          .sort((a, b) => a.id - b.id)
          .map(ord => resolveStatusLabel(ord?.status, config.value?.orders?.status, loc))
          .filter(Boolean)
          .join(', '),
    },
  ]
})

const lm = ref({ onPage: 5, page: 1 })

const query = reactive({
  order_field: 'date_created',
  order_direction: 'DESC',
  filter: '',
  filter_id: '',
  search: '',
  onPage: 5,
  page: 1,
})

const apiQuery = computed(() => {
  const base = {
    filter: query.filter,
    filter_id: query.filter_id,
    onPage: query.onPage,
    page: query.page,
    ...(query.search ? { search: query.search } : {}),
    ...(props.filter ? { [props.filter]: true } : {}),
  }

  if (query.filter === '' && query.filter_id === '') {
    return {
      ...base,
      order_field: query.order_field,
      order_direction: query.order_direction,
    }
  }

  return base
})

const { model, refresh } = useApi({
  url: 'activities',
  query: apiQuery,
  data: { data: [], total_count: 0 },
  pick: pick,
})

function pick(d) {
  const data = (d.data || []).map(d => {
    d.aligned_class = {
      'bi-check': d.aligned_by && d.aligned,
      'text-success': d.aligned_by && d.aligned,
      'bi-three-dots':
        (d.aligned_by == null && d.assign_role != null) || (d.aligned_by && !d.aligned),
      'text-warning': d.aligned_by == null && d.assign_role != null,
      'text-danger': d.aligned_by && !d.aligned,
    }
    if (d.shipment_date_scheduled) {
      d.shipment_date_scheduled = commons.dateLocal(d.shipment_date_scheduled)
    }

    d.order_out = { dispatch_date_executed: '' }
    const sortedOrders = [...(d.order || [])].sort((a, b) => a.id - b.id)
    if (sortedOrders.length > 0 && sortedOrders[0].dispatch_date_executed) {
      d.order_out.dispatch_date_executed = commons.dateLocal(
        sortedOrders[0].dispatch_date_executed
      )
    }

    return d
  })
  return { data, total_count: d.total_count ?? data.length }
}

function resolveStatusLabel(status, cfgList, loc) {
  if (!status) return ''
  const fromCfg = (cfgList || []).find(
    s => s.id == status.id || (status.key && s.key == status.key)
  )
  return dropdownLabel(fromCfg || status, loc)
}

function onRowClick(row) {
  router.push({ name: 'activities.item', params: { id: row.id } })
}

let prevFilter = null

watch(
  [lm, () => props.filter],
  ([val, filter]) => {
    Object.assign(query, val)

    if (prevFilter) {
      delete query[prevFilter]
    }

    if (filter) {
      query[filter] = true
    }

    prevFilter = filter

    refresh()
  },
  { immediate: true }
)

defineEmits(['modalstack:close'])
</script>
<i18n>
{
  "ru": { "title": "Активности, требующие от Вас действия", "approved": "Согласовано", "medicine": "Лекарственный препарат", "medicinalForm": "Лекарственная форма", "activity": "Активность", "activityType": "Тип активности", "responsible": "Ответственный", "plannedSubmissionDate": "Планируемая дата подачи", "actualSubmissionDate": "Фактическая дата подачи", "activityStatus": "Статус активности", "orderStatus": "Статус заявки" },
  "en": { "title": "Activities requiring your action", "approved": "Approved", "medicine": "Medicinal product", "medicinalForm": "Dosage form", "activity": "Activity", "activityType": "Activity type", "responsible": "Responsible person", "plannedSubmissionDate": "Planned submission date", "actualSubmissionDate": "Actual submission date", "activityStatus": "Activity status", "orderStatus": "Order status" }
}
</i18n>
